import React, { useEffect, useState } from 'react';
import { complianceService } from '../../services/api';

interface Group {
    id: string;
    name: string;
    children: Group[];
    propertyIds: string[];
}

interface GroupNavigatorProps {
    onSelect: (id: string) => void;
    activeId: string;
    refreshKey?: number;
}

const GroupNavigator: React.FC<GroupNavigatorProps> = ({ onSelect, activeId, refreshKey }) => {
    const [groups, setGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const fetchTree = async () => {
            try {
                const res = await complianceService.getPropertyTree();
                if (res.data?.success) {
                    setGroups(res.data.data);
                }
            } catch (err) {
                console.error('[GroupNavigator] Failed to fetch tree:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchTree();
    }, [refreshKey]);

    const toggleExpand = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const next = new Set(expandedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setExpandedIds(next);
    };

    const renderTree = (items: Group[], depth = 0) => {
        return items.map(group => {
            const isExpanded = expandedIds.has(group.id);
            const hasChildren = group.children && group.children.length > 0;

            return (
                <div key={group.id} className="hp-tree-container">
                    <div 
                        className={`hp-tree-item ${activeId === group.id ? 'active' : ''}`}
                        style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}
                        onClick={() => onSelect(group.id)}
                    >
                        <div className="flex items-center gap-2 overflow-hidden">
                            {hasChildren ? (
                                <button 
                                    className="p-1 hover:bg-slate-200 rounded transition-colors"
                                    onClick={(e) => toggleExpand(group.id, e)}
                                >
                                    <i className={`fas fa-chevron-right text-[10px] transition-transform ${isExpanded ? 'rotate-90' : ''}`}></i>
                                </button>
                            ) : (
                                <span className="w-5" />
                            )}
                            <i className={`fas ${hasChildren ? (isExpanded ? 'fa-folder-open' : 'fa-folder') : 'fa-building'} opacity-70 text-xs`}></i>
                            <span className="text-sm font-medium truncate">{group.name}</span>
                        </div>
                        <span className="text-[10px] font-bold opacity-60 bg-white bg-opacity-20 px-1.5 py-0.5 rounded">
                            {group.propertyIds.length}
                        </span>
                    </div>
                    {hasChildren && isExpanded && renderTree(group.children, depth + 1)}
                </div>
            );
        });
    };

    return (
        <div className="hp-card h-full flex flex-col">
            <h3 className="hp-nav-title">
                <i className="fas fa-sitemap text-slate-400"></i>
                Portfolio Filter
            </h3>

            <div 
                className={`hp-tree-item mb-2 ${activeId === 'all' ? 'active' : ''}`}
                onClick={() => onSelect('all')}
            >
                <div className="flex items-center gap-2">
                    <i className="fas fa-globe-europe opacity-70"></i>
                    <span className="text-sm font-bold">Entire Portfolio</span>
                </div>
            </div>

            <div className="hp-tree-scroller overflow-y-auto flex-1 pr-1 custom-scrollbar">
                {loading ? (
                    <div className="py-12 flex flex-col items-center">
                        <div className="hp-spinner-small mb-2"></div>
                        <p className="text-slate-400 text-[10px] italic">Mapping structure...</p>
                    </div>
                ) : groups.length > 0 ? (
                    renderTree(groups)
                ) : (
                    <div className="py-8 text-center text-slate-400 text-xs italic">
                        No groups configured.
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupNavigator;
